import type { FlyerImage } from './kit';

/**
 * How a product's photo stands on the flyer (adr/0050). A photo on white, or without a ground, is
 * blended on the page. Any other ground stays and the photo stands in a straight frame: cutting a
 * ground out needs a real model, and a colour fill eats stainless steel, whose shades are as
 * smooth as a wall behind it.
 */
export type PhotoGround = 'BLEND' | 'FRAME';

/** Lighter than this in every channel is white. */
const WHITE = 235;
/** The share of the picture's edge that makes it a photo on white; a product may touch the rest. */
const MIN_WHITE_EDGE = 0.6;
/** Enough pixels to read the edge, whatever the photo's size. */
const SAMPLE_SIDE = 200;

/** The ground of an RGBA picture, read from the pixels on its edge. */
export function findGround(rgba: Uint8ClampedArray, width: number, height: number): PhotoGround {
  let edge = 0;
  let plain = 0;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (x > 0 && y > 0 && x < width - 1 && y < height - 1) continue;
      const at = (y * width + x) * 4;
      const [r, g, b, a] = [rgba[at] ?? 0, rgba[at + 1] ?? 0, rgba[at + 2] ?? 0, rgba[at + 3] ?? 0];
      edge += 1;
      if (a < 16 || (r > WHITE && g > WHITE && b > WHITE)) plain += 1;
    }
  }
  return plain / edge >= MIN_WHITE_EDGE ? 'BLEND' : 'FRAME';
}

/** The product's photo ready for the flyer. An image the page may not read is framed. */
export function standPhoto(image: FlyerImage): FlyerImage {
  const scale = Math.min(1, SAMPLE_SIDE / Math.max(image.width, image.height));
  const width = Math.max(1, Math.round(image.width * scale));
  const height = Math.max(1, Math.round(image.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (ctx === null) return { ...image, ground: 'FRAME' };
  try {
    ctx.drawImage(image.source, 0, 0, width, height);
    const { data } = ctx.getImageData(0, 0, width, height);
    return { ...image, ground: findGround(data, width, height) };
  } catch {
    return { ...image, ground: 'FRAME' };
  }
}
